import { ref, onMounted, onUnmounted } from 'vue';
import { useAuth0 } from '@auth0/auth0-vue';
import { useTokenRefreshEvent } from './useTokenRefreshEvent';

const ACTIVITY_CHECK_INTERVAL_MS = 60 * 1000; // 60 seconds

/**
 * Composable to track user activity and extend session on mouse movement.
 * Automatically sets up mouse move listeners and session refresh intervals.
 */
export function useUserActivity() {
  const isUserActive = ref(false);
  let mouseMoveListener = null;
  let activityCheckInterval = null;

  const auth0 = useAuth0();
  const { emitTokenRefreshEvent } = useTokenRefreshEvent();

  const handleMouseMove = () => {
    isUserActive.value = true;
    removeMouseMoveListener();
  };

  const addMouseMoveListener = () => {
    if (!mouseMoveListener) {
      mouseMoveListener = handleMouseMove;
      window.addEventListener('mousemove', mouseMoveListener);
    }
  };

  const removeMouseMoveListener = () => {
    if (mouseMoveListener) {
      window.removeEventListener('mousemove', mouseMoveListener);
      mouseMoveListener = null;
    }
  };

  const checkActivityAndExtendSession = async () => {
    if (isUserActive.value) {
      try {
        await auth0.getAccessTokenSilently({ cacheMode: 'off' });
        isUserActive.value = false;
        addMouseMoveListener();
        emitTokenRefreshEvent();
      } catch (error) {
        console.error('Failed to extend session:', error);
        isUserActive.value = false;
        addMouseMoveListener();
      }
    }
  };

  onMounted(() => {
    addMouseMoveListener();
    activityCheckInterval = setInterval(checkActivityAndExtendSession, ACTIVITY_CHECK_INTERVAL_MS);
  });

  onUnmounted(() => {
    removeMouseMoveListener();
    if (activityCheckInterval) {
      clearInterval(activityCheckInterval);
      activityCheckInterval = null;
    }
  });

  return { isUserActive };
}
